import { z } from 'zod';
import { MoneySchema } from '../money.ts';
import { AuditSchema, DateOnlySchema, IdSchema } from './common.ts';

/**
 * Plant and tools a company owns, and where they are booked (SCRUM-209).
 *
 * The maintainer asked whether to integrate the existing FleetTrack app
 * (Expo + Supabase) over an API or copy it in. The answer taken, and the
 * reason it shapes this file: **bring the domain in, not the system.** The
 * feature actually wanted is "tell me when one machine is on two
 * overlapping job items", which is a question about Buildmate
 * `scheduleItems`, their dates and the job's work calendar — across an API
 * that is a cross-system join on every schedule edit. FleetTrack also has
 * its own companies, users and roles, so integrating would mean two
 * sources of truth for who is in a company. And a depreciation cost centre
 * is a **cost code**, which lives here.
 *
 * What is copied from FleetTrack is its *model*: the unit-name-plus-type
 * shape, the catalog of equipment types with typical sizes, make/model/
 * year/serial, and the next-maintenance date. What is **not** copied:
 *
 * - **`status` and `current_job_id` together.** FleetTrack stores a status
 *   of `available`/`assigned` alongside a `current_job_id`, and separately
 *   keeps an `equipment_assignments` table with its own dates. Three
 *   places saying where a machine is, which can disagree. Here `status` is
 *   **lifecycle only** — is this machine in service — and where it is
 *   comes from its bookings, derived, never stored.
 * - **Video inspections and repairs.** Separable, and not part of what was
 *   asked for. The register has to exist first.
 */
export const EQUIPMENT_STATUSES = ['active', 'maintenance', 'retired'] as const;
export const EquipmentStatusSchema = z.enum(EQUIPMENT_STATUSES);
export type EquipmentStatus = z.infer<typeof EquipmentStatusSchema>;

export const EQUIPMENT_STATUS_LABEL: Record<EquipmentStatus, string> = {
  active: 'In service',
  // Not "repair needed": a machine can be off the fleet for a service it is
  // not broken for, and a label that implies a fault misreports the common
  // case.
  maintenance: 'Out for maintenance',
  retired: 'Retired',
};

/**
 * A kind of machine, with what a company should know about it.
 *
 * Company-scoped rather than global, so a builder can add the one piece of
 * plant nobody else owns without waiting for us. Seeded from
 * `STARTER_EQUIPMENT_TYPES` when a company is created, the same way the
 * chart of accounts is.
 *
 * `typicalSizes` is free text on purpose. "20–50 ton", "3–8 yd³" and
 * "1,000 gal" are all answers, and an enum of capacities would be an enum
 * per category.
 */
export const EquipmentTypeSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  name: z.string().trim().min(1).max(120),
  category: z.string().trim().min(1).max(80),
  typicalSizes: z.string().trim().max(200).optional(),
  /** The icon FleetTrack drew for this type, kept so the two agree. */
  iconType: z.string().trim().max(60).optional(),
  /** Training an operator needs. **Shown, never enforced here.** */
  certification: z.string().trim().max(400).optional(),
  /**
   * The maintenance schedule, at the four intervals the source document
   * uses. Prose rather than a task list, because that is how it is
   * written and splitting it would be inventing structure it does not
   * have — `splitTasks` in FleetTrack does the same at render time.
   */
  dailyMaintenance: z.string().trim().max(1000).optional(),
  beforeUseInspection: z.string().trim().max(1000).optional(),
  afterUse: z.string().trim().max(1000).optional(),
  monthlyMaintenance: z.string().trim().max(1000).optional(),
  yearlyMaintenance: z.string().trim().max(1000).optional(),
  /**
   * The regulation this type's guidance cites.
   *
   * Carried per type and shown beside it. A maintenance schedule somebody
   * is asked to follow should say what it rests on, and a reader who
   * disagrees with a line needs somewhere to check it.
   */
  sourceUrl: z.string().trim().max(500).optional(),
  retiredAt: z.string().optional(),
});
export type EquipmentType = z.infer<typeof EquipmentTypeSchema>;

export const EquipmentSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  /**
   * What the crew calls it: `EX-04`, not `Caterpillar 320 GC`.
   *
   * The unit name is the thing people say on the radio and write on a daily
   * log, and two identical machines need telling apart — which is why this
   * is required and make/model are not.
   */
  name: z.string().trim().min(1, 'Give the unit a name or number').max(120),
  typeId: IdSchema,
  make: z.string().trim().max(120).optional(),
  model: z.string().trim().max(120).optional(),
  year: z.number().int().min(1900).max(2200).optional(),
  serialNumber: z.string().trim().max(120).optional(),
  sizeClass: z.string().trim().max(120).optional(),
  status: EquipmentStatusSchema.default('active'),
  /**
   * What it cost and when it was bought.
   *
   * Asked for explicitly, and the pair is the point: a cost with no date
   * cannot be depreciated. Integer minor units like every other amount in
   * this product.
   */
  acquiredOn: DateOnlySchema.optional(),
  acquisitionCost: MoneySchema.optional(),
  /**
   * The cost code depreciation is charged to.
   *
   * The maintainer asked for a "depreciation cost centre", and in this
   * product that is a cost code — which is why equipment lives in Firestore
   * beside the budget rather than in another system. Optional, because a
   * company that does not spread depreciation across jobs should not be
   * made to invent a code to save a machine.
   */
  depreciationCostCodeId: IdSchema.optional(),
  nextMaintenanceOn: DateOnlySchema.optional(),
  notes: z.string().trim().max(2000).optional(),
});
export type Equipment = z.infer<typeof EquipmentSchema>;

/** In service, so it can be booked onto work. */
export function isBookable(item: Pick<Equipment, 'status'>): boolean {
  return item.status === 'active';
}

/**
 * Whether a machine is due a service on or before a date.
 *
 * Inclusive, and absent means never due rather than overdue: a register
 * where every machine without a date screams for attention is a register
 * nobody reads.
 */
export function maintenanceDue(item: Pick<Equipment, 'nextMaintenanceOn'>, on: string): boolean {
  return item.nextMaintenanceOn !== undefined && item.nextMaintenanceOn <= on;
}

/**
 * How a machine reads in a list: `EX-04 · CAT 320 GC`.
 *
 * One function because three screens will want it — the register, the
 * booking picker and the schedule item — and three copies is three chances
 * for one of them to print a bare id.
 */
export function equipmentLabel(item: Pick<Equipment, 'name' | 'make' | 'model'>): string {
  const detail = [item.make, item.model].filter(Boolean).join(' ');
  return detail ? `${item.name} · ${detail}` : item.name;
}
